'use client';
import React from 'react';
import { ArrowRight, Sparkles, Play, Mic, Sun, RefreshCw, Moon } from 'lucide-react';
import Link from 'next/link';

export default function HeroSection() {
  return (
    <section className="hero-bg min-h-screen flex items-center pt-16 pb-12 overflow-hidden">
      {/* Background glow orbs */}
      <div
        className="absolute top-1/4 left-1/4 w-[700px] h-[700px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)', filter: 'blur(60px)' }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(167,139,250,0.05) 0%, transparent 70%)', filter: 'blur(50px)' }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div className="animate-fade-up">
            {/* Badge */}
            <div className="section-badge mb-6">
              <Sparkles size={12} />
              Your AI Business Partner for Real Estate
            </div>

            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6"
              style={{ color: '#F0F4FF' }}
            >
              Meet Your{' '}
              <span className="gradient-cyan-lavender-text">AI Business Partner</span>{' '}
              for Real Estate.
            </h1>

            {/* Subheadline */}
            <p
              className="text-lg leading-relaxed mb-10 max-w-xl"
              style={{ color: 'rgba(240,244,255,0.60)' }}
            >
              AgentOS helps real estate professionals prioritize their day, remember every client interaction, automate repetitive work and close more transactions through one intelligent AI conversation.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4">
              <a
                href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 text-base px-7 py-3.5"
              >
                Request Early Access
                <ArrowRight size={16} />
              </a>
              <Link
                href="/in-action"
                className="inline-flex items-center gap-2 text-base font-semibold px-7 py-3.5 rounded-xl transition-all"
                style={{
                  background: 'rgba(255,255,255,0.07)',
                  color: '#F0F4FF',
                  border: '1px solid rgba(255,255,255,0.14)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(0,212,255,0.10)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(0,212,255,0.25)';
                  (e.currentTarget as HTMLElement).style.color = '#00D4FF';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.07)';
                  (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.14)';
                  (e.currentTarget as HTMLElement).style.color = '#F0F4FF';
                }}
              >
                <Play size={15} />
                See AgentOS in Action
              </Link>
            </div>

            {/* No credit card note */}
            <p className="mt-5 text-sm" style={{ color: 'rgba(240,244,255,0.35)' }}>
              No credit card required. Cancel anytime.
            </p>

            {/* See in Action link */}
            <div className="mt-4">
              <Link
                href="/in-action"
                className="inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
                style={{ color: 'rgba(0,212,255,0.70)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#00D4FF'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = 'rgba(0,212,255,0.70)'; }}
              >
                See AgentOS in Action →
              </Link>
            </div>
          </div>

          {/* Right: AI Command Center Mockup */}
          <div className="relative animate-slide-in-right hidden lg:block">
            {/* Main Command Center Window */}
            <div className="mockup-window animate-float" style={{ animationDelay: '0s' }}>
              <div className="mockup-titlebar">
                <div className="mockup-dot" style={{ background: '#ff5f57' }} />
                <div className="mockup-dot" style={{ background: '#febc2e' }} />
                <div className="mockup-dot" style={{ background: '#28c840' }} />
                <span className="ml-3 text-xs font-medium" style={{ color: 'rgba(240,244,255,0.40)', fontFamily: 'JetBrains Mono, monospace' }}>AI Command Center</span>
                <div className="ml-auto flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs text-green-400" style={{ fontFamily: 'JetBrains Mono, monospace' }}>Listening</span>
                </div>
              </div>
              <div className="p-5" style={{ background: '#0B0F19' }}>
                {/* Morning Brief */}
                <div
                  className="rounded-xl p-4 mb-3"
                  style={{ background: 'rgba(0,212,255,0.07)', border: '1px solid rgba(0,212,255,0.15)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Sun size={14} style={{ color: '#00D4FF' }} />
                    <span className="text-xs font-bold" style={{ color: '#00D4FF', fontFamily: 'JetBrains Mono, monospace' }}>MORNING BRIEF</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: 'rgba(240,244,255,0.75)' }}>
                    Good morning. You have 3 follow-ups due today, 1 offer to review, and Sarah Johnson is expecting a call about 42 Maple St. Your highest-priority lead is Marcus Webb — he's been quiet for 8 days.
                  </p>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-3 gap-2 mb-3">
                  {[
                    { icon: Sun, label: 'Morning Brief', color: '#00D4FF' },
                    { icon: RefreshCw, label: 'Catch Me Up', color: '#A78BFA' },
                    { icon: Moon, label: 'Wrap Up', color: '#22C55E' },
                  ].map((action) => (
                    <div
                      key={action.label}
                      className="rounded-lg p-2.5 text-center cursor-pointer transition-all"
                      style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      <action.icon size={14} className="mx-auto mb-1" style={{ color: action.color }} />
                      <div className="text-xs" style={{ color: 'rgba(240,244,255,0.55)' }}>{action.label}</div>
                    </div>
                  ))}
                </div>

                {/* Voice Interaction */}
                <div
                  className="rounded-xl p-3"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center"
                      style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)' }}
                    >
                      <Mic size={10} className="text-black" />
                    </div>
                    <span className="text-xs italic" style={{ color: 'rgba(240,244,255,0.55)' }}>"Who should I call today?"</span>
                  </div>
                  <div
                    className="text-xs rounded-lg px-3 py-2"
                    style={{ background: 'rgba(0,212,255,0.08)', color: 'rgba(240,244,255,0.80)', border: '1px solid rgba(0,212,255,0.12)' }}
                  >
                    Call Marcus Webb first — he viewed 3 listings last night and hasn't responded to your last message. Then follow up with the Petersons on their offer timeline.
                  </div>
                </div>
              </div>
            </div>

            {/* Floating context card */}
            <div
              className="mockup-window absolute -bottom-6 -right-4 w-60"
              style={{ animation: 'float 6s ease-in-out 1.5s infinite' }}
            >
              <div className="mockup-titlebar">
                <div className="mockup-dot" style={{ background: '#ff5f57' }} />
                <div className="mockup-dot" style={{ background: '#febc2e' }} />
                <div className="mockup-dot" style={{ background: '#28c840' }} />
                <span className="ml-2 text-xs" style={{ color: 'rgba(240,244,255,0.40)', fontFamily: 'JetBrains Mono, monospace' }}>Memory</span>
                <div className="ml-auto flex items-center gap-1">
                  <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#A78BFA' }} />
                  <span className="text-xs" style={{ color: '#A78BFA', fontFamily: 'JetBrains Mono, monospace' }}>Active</span>
                </div>
              </div>
              <div className="p-3" style={{ background: '#0F1525' }}>
                <div className="text-xs mb-2 font-semibold" style={{ color: 'rgba(240,244,255,0.55)' }}>AI remembers everything</div>
                <div className="space-y-1.5">
                  {['Every client conversation', 'Every showing', 'Every promise made', 'Every follow-up due'].map((item) => (
                    <div key={item} className="flex items-center gap-1.5">
                      <div className="w-1 h-1 rounded-full" style={{ background: '#A78BFA' }} />
                      <span className="text-xs" style={{ color: 'rgba(240,244,255,0.50)' }}>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
